import { FakedCodelists } from '@administration/codelists/codelists.interface';
import { Codelists } from '@administration/codelists/codelists.service';
import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CodeList } from '@common/classes/codelist';
import { CommandParams } from '@common/interfaces/command-params.interface';
import { defaultSelectLabel } from '@common/models/util';
import { ViewMode } from '@common/models/view-mode';
import { DialogService } from '@common/services/dialog.service';
import { mapResultData } from '@ngneat/query';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { QueryService } from './query.service';
import { RememberStateService } from './remember-state.service';
import { ToastrNotificationService } from './toastr-notification.service';
import { UserService } from './user.service';
import { WindowFocusService } from './window-focus.service';

@Injectable()
export class CommonService {
    toastrNotificationService = inject(ToastrNotificationService);
    translateService = inject(TranslateService);
    router = inject(Router);
    activatedRoute = inject(ActivatedRoute);
    userService = inject(UserService);
    dialogService = inject(DialogService);
    queryService = inject(QueryService);
    rememberStateService = inject(RememberStateService);
    http = inject(HttpClient);
    injector = inject(Injector);
    spinner = inject(NgxSpinnerService);
    windowFocusService = inject(WindowFocusService);

    isViewMode() {
        return this.activatedRoute.snapshot.data.mode === ViewMode.view;
    }

    isEditMode() {
        return !this.isViewMode();
    }

    getSanitizedUrl(): string {
        const url = this.router.url;
        const id = this.activatedRoute.snapshot.paramMap.get('id');
        const mode = this.activatedRoute.snapshot.data.mode;

        if (id && mode) return url.replace(`/${mode}`, '').replace(`/${id}`, '');
        return url;
    }

    getCodelistItems(
        name: Codelists | FakedCodelists,
        filter: string = null,
        customFilter: string | number | boolean = null,
        selectLabel = (item: CodeList) => defaultSelectLabel(item)
    ) {
        return this.queryService
            .getCodelistQuery<any>({
                name,
                filter,
                customFilter,
                injector: this.injector
            })
            .result$.pipe(mapResultData((res) => res.map((g) => ({ value: g.id, label: selectLabel(g) }))));
    }

    async executeCommand(params: CommandParams): Promise<any> {
        const {
            command,
            data,
            customCallback,
            invalidate,
            successToastrMessage = 'Success',
            errorToastrMessage = 'An Error Occurred',
            showSpinner = false
        } = params;
        if (showSpinner) this.spinner.show();
        return this.queryService
            .getCommandMutation()
            .mutateAsync({ command, data, invalidate })
            .then(async (data) => {
                this.toastrNotificationService.show({ type: 'success', message: successToastrMessage });
                if (customCallback) await customCallback(data);
                return data;
            })
            .catch(() => {
                this.toastrNotificationService.show({ type: 'error', message: errorToastrMessage });
            })
            .finally(() => {
                if (showSpinner) this.spinner.hide();
            });
    }
}
